'use client';

import Link from 'next/link';
import { useCallback, useState, useMemo } from 'react';

import { StoneInvestmentPropertyCard } from './PropertyCard';
import type { FeaturedPlacementSummary } from '@/lib/api/clients/featured-content';
import { isHomepagePlacement } from '@/lib/api/clients/featured-content';

// Structured location from the Route Royal API
interface PropertyLocationData {
  city?: string | null;
  suburb?: string | null;
  district?: string | null;
  neighbourhood?: string | null;
  region?: string | null;
  country?: string | null;
  countryCode?: string | null;
  countryName?: string | null;
  localityName?: string | null;
  administrativeAreaName?: string | null;
}

interface Property {
  id: string;
  name: string;
  description?: string | null;
  status?: 'DRAFT' | 'ACTIVE' | 'INACTIVE' | 'ARCHIVED' | 'DELETED';
  primaryImageUrl?: string | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  totalFloorArea?: number | null;
  totalFloorAreaUnit?: string | null;
  currency?: string | null;
  location?: PropertyLocationData | null;
  attributeValues?: {
    askingPrice?: {
      valueNumber?: number | null;
      valueString?: string;
    };
  } | null;
  featuredPlacements?: FeaturedPlacementSummary[];
}

type Region = 'ALL' | 'FRANCE' | 'MAURITIUS';

export interface StoneInvestmentFeaturedPropertiesCarouselProps {
  properties: Property[];
  title?: string;
  subtitle?: string;
  onPropertyClick?: (property: Property) => void;
  maxItems?: number;
  messages?: Record<string, unknown>;
  locale?: 'en' | 'es' | 'fr';
}

/**
 * Detect region using structured location data from the API.
 */
const detectPropertyRegionFromProperty = (property: Property): 'FRANCE' | 'MAURITIUS' => {
  const loc = property.location;
  const country = (loc?.country || loc?.countryName || '').toLowerCase();
  const countryCode = (loc?.countryCode || '').toLowerCase();

  if (countryCode === 'mu' || country === 'mauritius') return 'MAURITIUS';
  return 'FRANCE';
};

/**
 * Stone Investment Featured Properties Carousel
 * 
 * Only displays properties that are featured for the Homepage section.
 * (Placement types: HOMEPAGE_SLIDER, HOMEPAGE_GRID, HOMEPAGE_SPOTLIGHT, etc.)
 * 
 * Concept One Design (Exact Match):
 * - Sticky filter bar with region tabs (All / Provence / Mauritius)
 * - Single column landscape cards with large gaps
 * - Property count display
 * - Private Sales CTA at bottom
 * - font-serif for headings, font-sans for body
 */
export function StoneInvestmentFeaturedPropertiesCarousel({
  properties,
  title: _title,
  subtitle: _subtitle,
  onPropertyClick,
  maxItems = 12,
  messages,
  locale = 'en',
}: StoneInvestmentFeaturedPropertiesCarouselProps) {
  const [activeRegion, setActiveRegion] = useState<Region>('ALL');

  const stoneMessages = (messages?.stone || {}) as Record<string, unknown>;
  const carouselMessages = (stoneMessages.featuredCarousel || {}) as Record<string, string>;
  const cardMessages = (stoneMessages.propertyCard || {}) as Record<string, string>;
  const tc = (key: string, fallback: string): string => carouselMessages[key] || fallback;
  
  // Filter to only properties featured for the homepage
  const homepageFeaturedProperties = useMemo(() => {
    const featured = properties.filter(p =>
      p.featuredPlacements?.some(fp => isHomepagePlacement(fp.placementType))
    );
    
    // Sort by priority (highest first)
    return featured.sort((a, b) => {
      const getPriority = (prop: Property) => {
        if (!prop.featuredPlacements) return 0;
        const homepagePlacement = prop.featuredPlacements.find(fp => 
          isHomepagePlacement(fp.placementType)
        );
        return homepagePlacement?.priority || 0;
      };
      return getPriority(b) - getPriority(a);
    }).slice(0, maxItems);
  }, [properties, maxItems]);

  // Filter properties by region
  const filteredProperties = useMemo(() => {
    if (activeRegion === 'ALL') return homepageFeaturedProperties;
    return homepageFeaturedProperties.filter(p => detectPropertyRegionFromProperty(p) === activeRegion);
  }, [homepageFeaturedProperties, activeRegion]);

  const handlePropertyClick = useCallback((property: Property) => {
    if (onPropertyClick) {
      onPropertyClick(property);
    }
  }, [onPropertyClick]);

  // Don't render if no homepage-featured properties
  if (homepageFeaturedProperties.length === 0) {
    return null;
  }

  return (
    <section className="relative min-h-screen bg-stone-50" id="properties">
      
      {/* Sticky Filter */}
      <div className="sticky top-0 z-40 bg-stone-50/95 backdrop-blur-md border-y border-stone-200 py-6 transition-all shadow-sm">
        <div className="container mx-auto px-6 md:px-12 flex flex-col md:flex-row justify-between items-center gap-6">
          <span className="hidden md:block text-sm uppercase tracking-widest text-stone-700 font-medium font-sans">
            {tc('theCollection', 'The Collection')}
          </span>
          
          {/* Region Tabs */}
          <div className="flex items-center gap-8">
            <button 
              onClick={() => setActiveRegion('ALL')}
              className={`text-sm uppercase tracking-widest transition-all duration-300 font-sans ${
                activeRegion === 'ALL' 
                  ? 'text-stone-900 font-bold scale-110' 
                  : 'text-stone-600 hover:text-stone-900'
              }`}
            >
              {tc('all', 'All')}
            </button>
            <span className="text-stone-400">/</span>
            <button 
              onClick={() => setActiveRegion('FRANCE')}
              className={`text-sm uppercase tracking-widest transition-all duration-300 font-sans ${
                activeRegion === 'FRANCE' 
                  ? 'text-stone-900 font-bold scale-110' 
                  : 'text-stone-600 hover:text-stone-900'
              }`}
            >
              {tc('provence', 'Provence')}
            </button>
            <span className="text-stone-400">/</span>
            <button 
              onClick={() => setActiveRegion('MAURITIUS')}
              className={`text-sm uppercase tracking-widest transition-all duration-300 font-sans ${
                activeRegion === 'MAURITIUS' 
                  ? 'text-stone-900 font-bold scale-110' 
                  : 'text-stone-600 hover:text-stone-900'
              }`}
            >
              {tc('mauritius', 'Mauritius')}
            </button>
          </div>

          {/* Property Count */}
          <div className="hidden md:block text-sm uppercase tracking-widest text-stone-700 font-medium w-24 text-right font-sans">
            {filteredProperties.length} {tc('estates', 'Estates')}
          </div>
        </div>
      </div>

      {/* The Grid - Single Column Landscape */}
      <div className="container mx-auto px-6 md:px-12 py-24">
        <div className="flex flex-col gap-32 max-w-6xl mx-auto">
          {filteredProperties.map((property, idx) => (
            <div 
              key={property.id} 
              className="w-full transition-all duration-700 ease-out hover:-translate-y-2"
            >
              <StoneInvestmentPropertyCard 
                property={property} 
                index={idx} 
                {...(onPropertyClick && { onClick: () => handlePropertyClick(property) })}
                variant="landscape"
                locale={locale}
                {...(property.featuredPlacements ? { featuredPlacements: property.featuredPlacements } : {})}
                showFeaturedBadge
                translations={cardMessages}
              />
            </div>
          ))}
        </div>
        
        {/* Empty State */}
        {filteredProperties.length === 0 && (
          <div className="py-24 text-center">
            <p className="font-serif text-2xl text-stone-700 italic">
              {tc('noProperties', 'No properties found in this region.')}
            </p>
            <button 
              onClick={() => setActiveRegion('ALL')} 
              className="mt-4 text-xs uppercase tracking-widest text-stone-900 border-b border-stone-900 pb-1 font-sans"
            >
              {tc('viewAll', 'View All')}
            </button>
          </div>
        )}
        
        {/* Private Sales CTA */}
        <div className="mt-40 flex flex-col items-center justify-center space-y-8 hover:opacity-80 transition-opacity">
          <div className="w-px h-24 bg-stone-400" />
          <p className="font-serif text-stone-800 italic text-xl">
            {tc('privateSales', 'Private Sales')}
          </p>
          <p className="font-sans font-light text-stone-700 text-sm max-w-md text-center">
            {tc('privateSalesDescription', 'Some of our most exclusive properties are not listed online. Contact our private office for off-market opportunities.')}
          </p>
          <Link 
            href={`/${locale}/contact`}
            className="px-8 py-3 border border-stone-300 text-stone-900 uppercase tracking-widest text-xs hover:bg-stone-900 hover:text-white transition-colors font-sans"
          >
            {tc('accessPrivateFile', 'Access Private File')}
          </Link>
        </div>
      </div>
    </section>
  );
}
